"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { calculateLegalReadingProgress } from "@/src/features/legal/legalReadingProgress";

export function decodeLegalHash(rawHash: string): string | null {
  try {
    return decodeURIComponent(rawHash);
  } catch {
    return null;
  }
}

export function useLegalDocScroll() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    setProgress(calculateLegalReadingProgress(scrollTop, scrollHeight, clientHeight));

    const sectionEls = el.querySelectorAll<HTMLElement>("[data-section]");
    let current = 0;
    sectionEls.forEach((sec, i) => {
      if (sec.getBoundingClientRect().top <= 200) current = i;
    });
    setActiveIndex(current);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const scrollToCurrentHash = () => {
      const rawHash = window.location.hash.slice(1);
      if (!rawHash) {
        requestAnimationFrame(() => {
          const container = scrollRef.current;
          if (!container) return;
          // Browser history restores the document scroll position, but these
          // legal documents scroll inside their own container. Force an
          // instant top restoration when Back removes the section hash, then
          // let the page's normal smooth-scroll preference resume.
          const previousScrollBehavior = container.style.scrollBehavior;
          container.style.scrollBehavior = "auto";
          container.scrollTop = 0;
          handleScroll();
          requestAnimationFrame(() => {
            container.style.scrollBehavior = previousScrollBehavior;
          });
        });
        return;
      }
      const id = decodeLegalHash(rawHash);
      if (!id) return;
      requestAnimationFrame(() => {
        const container = scrollRef.current;
        const target = document.getElementById(id);
        if (!container || !target) return;
        const top = (
          target.getBoundingClientRect().top
          - container.getBoundingClientRect().top
          + container.scrollTop
          - 96
        );
        container.scrollTop = Math.max(0, top);
        requestAnimationFrame(handleScroll);
      });
    };

    scrollToCurrentHash();
    window.addEventListener("hashchange", scrollToCurrentHash);
    return () => window.removeEventListener("hashchange", scrollToCurrentHash);
  }, [handleScroll]);

  return { scrollRef, activeIndex, progress };
}
